// Central SEO config for every public route on whalecoveoffgrid.ca
// Edit titles/descriptions here — they appear in Google, social shares, and browser tabs.

export const SITE = {
  name: "Whale Cove Off Grid",
  url: "https://whalecoveoffgrid.ca",
  // 1200x630 social share card — proxied/resized from hero photo until a custom card is uploaded.
  defaultOgImage:
    "https://wsrv.nl/?url=customer-assets.emergentagent.com/job_whale-cove-retreat/artifacts/0zj0zjrb_IMG_7577.jpeg&w=1200&h=630&fit=cover&output=jpg&q=82",
  twitterHandle: "", // add if a Twitter/X handle is ever created
  locale: "en_CA",
  // Brand colour for theme-color / address bar
  themeColor: "#2C3828",
};

// Geographic & business data used by LodgingBusiness structured data on the home page.
export const BUSINESS = {
  name: "Whale Cove Off Grid",
  streetAddress: "Whale Cove Road",
  addressLocality: "Digby Neck",
  addressRegion: "Nova Scotia",
  postalCode: "",
  addressCountry: "CA",
  latitude: 44.430694,
  longitude: -66.173889,
  email: "whalecoveoffgrid@gmail.com",
  description:
    "A private, off-grid RV and trailer retreat on the Bay of Fundy in Digby Neck, Nova Scotia. Up to 725 ft of waterfront, 18.5 acres of woodland, Bortle Class 2 dark skies, and 21-ft Fundy tides.",
  amenities: [
    "Oceanfront",
    "Off-grid",
    "Pet-friendly",
    "Whale watching from shore",
    "Dark-sky stargazing",
    "Private booking — one group at a time",
    "Wooded trails",
    "Bay of Fundy tides",
  ],
};

// Per-route SEO. Keys are pathnames. The "*" key is the home page (also catches unknown routes).
export const PAGE_META = {
  "/": {
    title: "Whale Cove Off Grid — Private RV Site on the Bay of Fundy, Digby Neck NS",
    description:
      "A private off-grid RV site on the Bay of Fundy near Digby, Nova Scotia. 18.5 acres, up to 725 ft of oceanfront, Bortle Class 2 dark skies, and the world's highest tides — your group, one site at a time.",
    keywords: [
      "private RV site Bay of Fundy",
      "off-grid camping Nova Scotia",
      "camping near Digby",
      "RV camping Digby Neck",
      "oceanfront RV camping Nova Scotia",
      "Bay of Fundy RV rental",
      "Whale Cove Off Grid",
    ],
    type: "website",
  },
  "/story": {
    title: "Our Story — Building a Private RV Site on the Bay of Fundy",
    description:
      "From a chance walk along the Bay of Fundy to a private off-grid RV site on Digby Neck — the five-chapter origin of Whale Cove Off Grid in Nova Scotia.",
    keywords: [
      "Whale Cove story",
      "Bay of Fundy retreat origin",
      "private RV site Nova Scotia",
      "Digby Neck off-grid",
    ],
    type: "article",
  },
  "/land": {
    title: "The Land — Oceanfront RV Site on the Bay of Fundy, Nova Scotia",
    description:
      "18.5 acres of Bay of Fundy waterfront across two private RV sites. Site One (3.5 acres, 175 ft) and Site Two (15 acres, 550 ft) — oceanfront RV camping near Digby, NS, booked one group at a time.",
    keywords: [
      "oceanfront RV camping Nova Scotia",
      "waterfront campsite Nova Scotia",
      "Bay of Fundy waterfront property",
      "private RV site Nova Scotia",
      "private campground Nova Scotia",
      "Digby Neck land",
    ],
    type: "website",
  },
  "/environment": {
    title: "Tides & Skies — Dark Sky Camping on the Bay of Fundy",
    description:
      "A live ambient dashboard for Whale Cove Off Grid: Bay of Fundy tides, marine weather, golden hour, moon phase, and aurora forecasts. Bortle Class 2 dark sky camping in the Maritimes.",
    keywords: [
      "dark sky camping Maritimes",
      "stargazing Nova Scotia",
      "Bortle Class 2 Nova Scotia",
      "Bay of Fundy tide times",
      "Digby Neck weather",
      "aurora forecast Nova Scotia",
    ],
    type: "website",
  },
  "/offgrid": {
    title: "Off-Grid Camping in Nova Scotia — Whale Cove on the Bay of Fundy",
    description:
      "Off-grid camping in Nova Scotia for self-contained RVs and trailers. No hookups, no neighbours, just the Bay of Fundy. Boondocking on Digby Neck — generator-friendly, pet-friendly, RV-only (no tent camping).",
    keywords: [
      "off-grid camping Nova Scotia",
      "boondocking Nova Scotia",
      "self-contained RV camping",
      "off-grid RV site Canada",
      "wilderness camping Digby Neck",
    ],
    type: "website",
  },
  "/activities": {
    title: "Activities Near Brier Island — Whales, Trails & Bay of Fundy",
    description:
      "Camping near Brier Island and Digby Neck — whale watching from shore, the Tiverton ferry, Balancing Rock on Long Island, dark-sky stargazing, beachcombing, and forest trails from Whale Cove Off Grid.",
    keywords: [
      "camping near Brier Island",
      "things to do Digby Neck",
      "Brier Island whale watching",
      "Balancing Rock Long Island",
      "Bay of Fundy activities",
      "Tiverton ferry",
    ],
    type: "website",
  },
  "/whales": {
    title: "Whale Watching Accommodation on the Bay of Fundy — Whale Cove Off Grid",
    description:
      "Whale watching accommodation on the Bay of Fundy. Live humpback, fin, and minke sightings from Whale Cove Off Grid on Digby Neck — stay near Brier Island whale watching, see them from shore.",
    keywords: [
      "whale watching accommodation Bay of Fundy",
      "stay near Brier Island whale watching",
      "Bay of Fundy whale watching",
      "Digby Neck whale sightings",
      "humpback whale Bay of Fundy",
      "North Atlantic right whale",
    ],
    type: "website",
  },
  "/gallery": {
    title: "Photography — The Bay of Fundy at Whale Cove Off Grid",
    description:
      "An editorial gallery of Whale Cove Off Grid: oceanfront RV site, tides, light, whales, and the wild Atlantic edge of Digby Neck, Nova Scotia.",
    keywords: [
      "Bay of Fundy photography",
      "Digby Neck photos",
      "oceanfront campsite Nova Scotia",
    ],
    type: "website",
  },
  "/videos": {
    title: "Videos — Whale Cove Off Grid on the Bay of Fundy",
    description:
      "Short films from Whale Cove Off Grid: tides, whales, weather, and the rhythm of off-grid RV camping on the Bay of Fundy in Nova Scotia.",
    keywords: ["Bay of Fundy video", "off-grid RV Nova Scotia"],
    type: "website",
  },
  "/faq": {
    title: "FAQ — RV Camping on Digby Neck at Whale Cove Off Grid",
    description:
      "RV camping on Digby Neck — answers to the common questions about staying at Whale Cove Off Grid on the Bay of Fundy: RV requirements, dog-friendly camping in Nova Scotia, off-grid power, tides, whales, and getting here.",
    keywords: [
      "RV camping Digby Neck",
      "dog-friendly camping Nova Scotia",
      "pet-friendly RV camping",
      "off-grid RV questions",
      "Whale Cove FAQ",
    ],
    type: "website",
  },
  "/guestbook": {
    title: "Guestbook — Notes from Whale Cove Off Grid, Bay of Fundy",
    description:
      "Notes, photos, and reflections from guests of Whale Cove Off Grid on the Bay of Fundy, Digby Neck, Nova Scotia.",
    keywords: ["Whale Cove guestbook", "Bay of Fundy guest reviews"],
    type: "website",
  },
  "/waitlist": {
    title: "Follow the Build — Whale Cove Off Grid (Opens 2027 Season)",
    description:
      "Follow the Whale Cove Off Grid build on the Bay of Fundy near Digby, Nova Scotia. Site One opens for the 2027 season — Site Two is a longer-term build. Through 2026 we're on the land ourselves, testing everything with family and friends.",
    keywords: [
      "private RV site reservation",
      "camping near Digby NS",
      "Bay of Fundy retreat waitlist",
      "oceanfront RV rental Nova Scotia",
    ],
    type: "website",
  },
  "/booking": {
    title: "Book Your Stay — Whale Cove Off Grid, Bay of Fundy",
    description:
      "Reserve a private off-grid RV site on the Bay of Fundy at Whale Cove on Digby Neck, Nova Scotia.",
    keywords: ["book Bay of Fundy RV", "reserve Whale Cove", "private RV site"],
    type: "website",
  },
};

// FAQ structured data (FAQPage schema). Mirrors the most-asked questions on /faq.
// Keep these in sync with the visible FAQ on the page.
export const FAQ_SCHEMA_ITEMS = [
  {
    q: "Do I need to bring my own RV or trailer?",
    a: "Yes. Whale Cove Off Grid is a bring-your-own RV / trailer / camper retreat. The site has no rental units — guests stay in their own self-contained unit.",
  },
  {
    q: "Is the site fully off-grid?",
    a: "Yes — there are no hookups for power, water, or sewer. You'll need a self-contained RV and a generator. We can guide you on what works best for the site.",
  },
  {
    q: "Are pets allowed?",
    a: "Yes. Whale Cove Off Grid is pet-friendly. Bring your dog. The 18.5 acres of woodland and shoreline are perfect for them — and there are no neighbours.",
  },
  {
    q: "Can you see whales from the property?",
    a: "Yes. The Bay of Fundy is one of the richest whale habitats on Earth. Humpback, fin, minke, and (rarely) North Atlantic right whales pass within view of the shore at Whale Cove from late spring through fall.",
  },
  {
    q: "Where exactly is Whale Cove Off Grid?",
    a: "On Digby Neck, a long peninsula stretching into the Bay of Fundy in southwestern Nova Scotia, Canada. About a 45-minute drive from the town of Digby and the ferry to Saint John, New Brunswick.",
  },
  {
    q: "When does it open?",
    a: "Site One opens for guests in the 2027 season. Through 2026 we're on the land ourselves — hosting family and friends to test every detail, and walking permits through Digby Municipality so everything is fully approved before we host a paying guest. Site Two is a longer-term build (the access road is a major project) and will follow in future seasons. Join the Follow-the-Build list for updates.",
  },
];
